import { useEffect, useMemo, useState } from 'react';
import { getRankingHome, type HomeRanking, type RankPlayer } from '@/shared/services/rankingService';
import { CLASSES_PT } from '@/features/Admin/labels';
import { NATION_NAME, formatScore, useNextWar } from '@/shared/lib/war';
import { SectionTitle } from './SectionTitle';

const NATION_IMG: Record<number, string> = { 1: '/assets/liberty/capella.webp', 2: '/assets/liberty/procyon.webp' };
const NATION_MOTTO: Record<number, string> = { 1: 'O poder da luz', 2: 'A força do caos' };

// Arte _d por classe; algumas ancoram melhor fora do topo (mesmo ajuste da home antiga).
const CLASS_OBJ_POS: Record<number, string> = { 1: 'right bottom', 2: 'right top', 5: 'right top', 6: 'right top', 7: 'right top' };

type Hero = { p: RankPlayer; kind: 'Portador' | 'Memorial' };

// Cor de identidade de cada nacao (mesma da home antiga): tags, borda e brilho do hover.
const NATION_COLOR: Record<number, string> = { 1: '#ca69d3', 2: '#2a7ddd' };

function WarPlayerCard({ hero }: { hero: Hero }) {
  const { p, kind } = hero;
  const meta = [CLASSES_PT[p.classId] || '—', p.level ? `Lv. ${p.level}` : null, p.guildName ? `< ${p.guildName} >` : null]
    .filter(Boolean).join(' · ');
  return (
    <article className="war-player war-hero fantasy-frame" style={{ ['--c' as string]: NATION_COLOR[p.nation] ?? '#aab4bf' }}>
      <img src={`/assets/class/${p.classId}_d.webp`} alt={p.characterName} style={{ objectPosition: CLASS_OBJ_POS[p.classId] ?? 'top' }} />
      <div className="war-grad"></div>
      <div className="war-player-info">
        <div>
          <span className={`tag-kind${kind === 'Memorial' ? ' memorial' : ''}`}>{kind}</span>
          <span className="tag-nation">{NATION_NAME[p.nation] ?? '—'}</span>
        </div>
        <h3>{p.characterName}</h3>
        <p>{meta}</p>
        <strong>{p.combat ? formatScore(p.combat) : '—'} <small>CP</small></strong>
      </div>
    </article>
  );
}

export function WarSection() {
  const [data, setData] = useState<HomeRanking | null>(null);
  const war = useNextWar();

  useEffect(() => {
    getRankingHome().then(setData).catch(() => setData(null));
  }, []);

  const winner = data?.nation?.victoryNation || 0;
  const left = winner || 1;              // vencedor fica a esquerda (Capella no layout)
  const right = left === 1 ? 2 : 1;
  const countWar = data?.countWar ?? 0;

  // Portadores do vencedor -> memorial do vencedor -> portadores/memorial do oponente.
  const heroes: Hero[] = useMemo(() => {
    const portadores = data?.portadores ?? [];
    const memorial = data?.memorial ?? [];
    const by = (arr: RankPlayer[], n: number, kind: Hero['kind']) => arr.filter((x) => x.nation === n).map((p) => ({ p, kind }));
    return [...by(portadores, left, 'Portador'), ...by(memorial, left, 'Memorial'), ...by(portadores, right, 'Portador'), ...by(memorial, right, 'Memorial')];
  }, [data, left, right]);

  const winnerName = winner ? NATION_NAME[winner] : '—';

  return (
    <section className="section war" id="guerra">
      <div className="shell">
        <SectionTitle
          title="Memorial & Guerra"
          subtitle="Os guerreiros que decidem o destino da guerra."
          /* Historico de guerras: pagina ainda nao existe — religar quando existir.
          action={<a className="outline-btn" href="#ranking">Histórico de guerras <span>›</span></a>}
          */
        />

        <div className="war-summary fantasy-frame reveal">
          <div className="war-faction war-capella">
            <img src={NATION_IMG[left]} alt={NATION_NAME[left]} />
            <div><small>{winner ? 'Último vencedor' : 'Nação'}</small><strong>{NATION_NAME[left]}</strong><span>{NATION_MOTTO[left]}</span></div>
          </div>
          <div className="war-progress">
            <small>Próxima guerra</small>
            <strong>Às {war.hour}</strong>
            <b>{war.long}</b>
            <div className="war-meter"><i style={{ width: `${Math.round(war.progress * 100)}%` }}></i></div>
          </div>
          <div className="war-faction war-procyon">
            <div><small>Oponente</small><strong>{NATION_NAME[right]}</strong><span>{NATION_MOTTO[right]}</span></div>
            <img src={NATION_IMG[right]} alt={NATION_NAME[right]} />
          </div>
        </div>

        <div className="war-meta fantasy-frame reveal">
          <div><small>Último vencedor</small><strong>{winnerName}</strong></div>
          <div><small>Consecutivas</small><strong>{countWar}</strong></div>
          <div><small>Próxima guerra em</small><strong>{war.long}</strong><span>às {war.hour}</span></div>
        </div>

        {/* Portadores + memorial: 1 card por guerreiro real, faixa com scroll horizontal.
            SEM .reveal: a faixa so' nasce depois do fetch e o useReveal observa so' o que existe
            no mount -> ficava opacity 0 (o card do portador "sumia"). */}
        {heroes.length > 0 ? (
          <div className="war-heroes lb-scroll">
            {heroes.map((h, i) => <WarPlayerCard key={`${h.kind}-${h.p.characterName}-${i}`} hero={h} />)}
          </div>
        ) : (
          <p className="war-empty">Sem dados de guerra disponíveis</p>
        )}
      </div>
    </section>
  );
}
